import { DATE_TIME_FORMAT_DEFAULT, DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import ListingTableCache, { ListingTableData } from 'common/components/listingTable/ListingTableCache';
import TableActionButton from 'common/components/listingTable/actionButton/TableActionButton';
import { FilterData, ListingTableColumn, OrderData } from 'common/components/listingTable/models';
import { orderColumns } from 'common/components/listingTable/tableUtils';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaTrash } from 'react-icons/fa';
import Logger from 'common/services/Logger'
import toast from 'react-hot-toast';
import ListingTable from 'common/components/listingTable/ListingTable';
import ListingTableSidebar from 'common/components/listingTable/sidebar/ListingTableSidebar';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import Loading from 'common/services/Loading';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import Button from 'common/components/button/Button';
import DateFormat from 'common/components/dateFormat/dateFormat';
import { BudgetFileModelDto } from 'api/budgetFileModels/models/BudgetFileModelDto';
import { BudgetFileModelSearchCriteria } from 'api/budgetFileModels/models/BudgetFileModelSearchCriteria';
import BudgetFileModelsService from 'api/budgetFileModels/BudgetFileModelsService';

const PAGE_ID = 'BACKOFFICE_BUDGET_FILE_MODELS_LIST';

function BudgetFileModelsListScreen(): JSX.Element | null {
    const { t } = useTranslation();
    const [tableData, setTableData] = useState<ListingTableData<BudgetFileModelDto, Partial<BudgetFileModelSearchCriteria>>>(ListingTableCache.get(PAGE_ID, {
        order: { field: 'name', orderColumn: 'bfm.name', isOrderAsc: true },
        hiddenColumns: [],
        columnsOrder: [],
        columnsWidth: [],
        page: 1,
        filters: {},
        lastRowId: null,
    }));
    const url = '/backoffice/budget-file-models';

    const [models, setModels] = useState<BudgetFileModelDto[]>([]);
    const [modelToDelete, setModelToDelete] = useState<BudgetFileModelDto | null>(null);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGETS_FILE_MODELS_WRITE'])
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGETS_FILE_MODELS_READ'])
    const [refreshKey, setRefreshKey] = useState(0);
    const [scrollToRowId, setScrollToRowId] = useState<string | null>(null);
    const firstLoad = useRef(true);

    const columns: ListingTableColumn<BudgetFileModelDto>[] = orderColumns([
        {
            name: t('budget_file_models.name'),
            field: 'name',
            filter: { type: 'text', value: tableData.filters.name ?? '', placeholder: t('budget_file_models.name') },
            minWidth: '18rem',
        },
        {
            name: t('common.updated_date'),
            field: 'updatedDate',
            filter: {
                type: 'date-range',
                value: {
                    start: tableData.filters.updatedDate?.start ? new Date(tableData.filters.updatedDate.start) : null,
                    end: tableData.filters.updatedDate?.end ? new Date(tableData.filters.updatedDate.end) : null
                }
            },
            minWidth: '22rem',
            orderColumn: 'updated_date',
            renderCell: (row) => {
                return row.updatedDate ? <DateFormat value={row.updatedDate} format={DATE_TIME_FORMAT_DEFAULT} /> : '';
            }
        },
        {
            name: t('common.user_updated'),
            field: 'userUpdatedName',
            filter: { type: 'text', value: tableData.filters.userUpdatedName ?? '', placeholder: t('common.user_updated') },
            minWidth: '21rem',
            orderColumn: 'user_updated_name',
        },
    ], tableData.columnsOrder);

    const renderAction = (row: BudgetFileModelDto) => (
        <TableActionButton
            options={[
                { label: <span><FaEdit /> {t('common.edit')}</span>, href: `${url}/edit/${row.id}`, onClick: () => ListingTableCache.saveLastRowId(PAGE_ID, row.id) },
                { label: <span><FaTrash /> {t('common.delete')}</span>, onClick: () => onOpenModalToDelete(row) },
            ]}
        />
    )

    useEffect(() => {
        void getData();
    }, [tableData.page, tableData.filters, tableData.order, refreshKey]);

    useEffect(() => {
        ListingTableCache.save(PAGE_ID, tableData);
    }, [tableData]);

    const getData = async () => {
        if (isLoading) {
            return;
        }

        try {
            setIsLoading(true);
            const modelsData = await BudgetFileModelsService.getList({
                ...tableData.filters,
                page: firstLoad.current && tableData.page > 1 ? 1 : tableData.page,
                itemsPerPage: firstLoad.current && tableData.page > 1 ? DEFAULT_PAGINATION_ITEMS_PER_PAGE * tableData.page : DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                orderColumn: tableData.order.orderColumn,
                orderBy: tableData.order.isOrderAsc ? 'asc' : 'desc'
            });

            const newListElements = modelsData.currentPage === 1 ? modelsData.items : [...models, ...modelsData.items]
            setHasMore(modelsData.totalItems > newListElements.length);
            setModels(newListElements);
            setTotalItems(modelsData.totalItems)

            if (firstLoad.current) {
                setScrollToRowId(tableData.lastRowId);
            }
            firstLoad.current = false;
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get budget file models list', error);
            toast.error(t('messages.error_load_info'));
        }

        setIsLoading(false);
    }

    const onRowClick = (row: BudgetFileModelDto) => {
        ListingTableCache.saveLastRowId(PAGE_ID, row.id);
    }

    const onOpenModalToDelete = (row: BudgetFileModelDto) => {
        setModelToDelete(row);
        setShowConfirmDeleteModal(true)
    }

    const onDelete = async (result: boolean) => {
        if (!result) {
            setShowConfirmDeleteModal(false)
            return;
        }
        try {
            await BudgetFileModelsService.remove(modelToDelete as BudgetFileModelDto)
            setTableData(d => ({ ...d, page: 1 }));
            setRefreshKey(k => k + 1);
            Loading.hide();
            toast.success(t('messages.record_delete_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete budget file model', error);
            toast.error(t('messages.record_delete_error'));
        }
        setShowConfirmDeleteModal(false)
    }

    const onLoadMore = () => {
        setTableData(d => ({ ...d, page: d.page + 1 }));
    }

    const onFilter = (col: ListingTableColumn<BudgetFileModelDto>, filterData: FilterData) => {
        setTableData(d => {
            const data = ({
                ...d, page: 1
            })
            if (col.field === 'name') {
                data.filters = {
                    ...data.filters,
                    name: filterData.value as string,
                };
            }
            if (col.field === 'updatedDate') {
                data.filters = {
                    ...data.filters,
                    updatedDate: filterData.value,
                    updatedDateStart: filterData.value?.start as Date,
                    updatedDateEnd: filterData.value?.end as Date
                };
            }
            if (col.field === 'userUpdatedName') {
                data.filters = {
                    ...data.filters,
                    userUpdatedName: filterData.value as string
                };
            }
            return data;
        });
        setRefreshKey(k => k + 1);
    }

    const onOrder = (orderData: OrderData<BudgetFileModelDto>) => {
        setRefreshKey(k => k + 1)
        setTableData(d => ({ ...d, order: orderData, page: 1 }));
    }

    const onClearFilters = () => {
        setTableData(d => ({ ...d, page: 1, filters: {}, lastRowId: null }));
        setRefreshKey(k => k + 1)
    }

    if (!canRead) {
        return null;
    }

    return (<ScreenTitle title={t('budget_file_models.title')}>
        <PageBreadcrumbsPortal
            breadcrumbs={[
                { name: t('home.title'), url: '/' },
                { name: t('budget_file_models.title'), url },
            ]}
        />
        <PageHeader title={t('budget_file_models.title')} subTitle={`${totalItems} ${t('common.total_results')}`} informationText={t('common.list')} addSidebarSpacing >
            {canWrite && <Button href={`${url}/create`}>{t('common.new')}</Button>}
        </PageHeader>
        <PageContainer>
            <ListingTable<BudgetFileModelDto>
                columns={columns}
                rows={models}
                order={tableData.order}
                hiddenColumns={tableData.hiddenColumns}
                columnsWidths={tableData.columnsWidth}
                onOrder={onOrder}
                onFilter={onFilter}
                onClearFilters={onClearFilters}
                onRenderAction={canWrite ? renderAction : undefined}
                onRowClick={onRowClick}
                onHref={(row) => `${url}/details/${row.id}`}
                infiniteScroll={{
                    isLoading,
                    hasMore,
                    onLoadMore,
                }}
                onRowId={r => r.id}
                scrollToRowId={scrollToRowId}
                onColResize={(w) => setTableData(d => ({ ...d, columnsWidth: w }))}
            />
        </PageContainer>
        <ListingTableSidebar
            columns={columns}
            hiddenColumns={tableData.hiddenColumns}
            onChangeHiddenColumns={(hc) => setTableData(d => ({ ...d, hiddenColumns: hc }))}
            onChangeColumnsOrder={(oc) => setTableData(d => ({ ...d, columnsOrder: oc }))}
        />
        <ConfirmDeleteModal
            itemName={modelToDelete?.name}
            isOpen={showConfirmDeleteModal}
            onClose={(result) => onDelete(result)}
        />
    </ScreenTitle>)
}

export default BudgetFileModelsListScreen;
